<script lang="ts">
  import { onMount } from 'svelte';

  let lamp = $state<HTMLDivElement | null>(null);
  let lit = $state(false);

  onMount(() => {
    if (!lamp) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        lit = true;
        observer.disconnect();
      },
      { threshold: 0.35 }
    );
    observer.observe(lamp);
    return () => observer.disconnect();
  });
</script>

<div class="lamp" class:lit bind:this={lamp} aria-hidden="true">
  <div class="cone cone-left"></div>
  <div class="cone cone-right"></div>
  <div class="halo"></div>
  <div class="bar"></div>
  <div class="floor"></div>
</div>

<style>
  .lamp {
    --lamp-colour: #7d98ff;
    --cone-width: 15rem;
    --bar-width: 15rem;
    --halo-width: 8rem;
    position: absolute;
    inset: 0 0 auto;
    height: 420px;
    pointer-events: none;
    isolation: isolate;
  }
  .lamp.lit {
    --cone-width: 30rem;
    --bar-width: 30rem;
    --halo-width: 16rem;
  }
  .cone {
    position: absolute;
    top: 0;
    width: var(--cone-width);
    height: 280px;
    opacity: 0.5;
    transition:
      width 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s,
      opacity 1.4s ease 0.2s;
    mask-image: linear-gradient(to bottom, #000 0%, transparent 92%);
  }
  .lit .cone {
    opacity: 1;
  }
  .cone-left {
    right: 50%;
    background: conic-gradient(from 70deg at center top, var(--lamp-colour), transparent 22%);
    mask-image: linear-gradient(to bottom, #000 0%, transparent 92%), linear-gradient(to right, transparent, #000 40%);
    mask-composite: intersect;
  }
  .cone-right {
    left: 50%;
    background: conic-gradient(from 290deg at center top, transparent 78%, var(--lamp-colour));
    mask-image: linear-gradient(to bottom, #000 0%, transparent 92%), linear-gradient(to left, transparent, #000 40%);
    mask-composite: intersect;
  }
  .halo {
    position: absolute;
    top: -40px;
    left: 50%;
    width: var(--halo-width);
    height: 140px;
    translate: -50% 0;
    border-radius: 999px;
    background: #6c8cff;
    filter: blur(48px);
    opacity: 0.55;
    transition: width 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s;
  }
  .bar {
    position: absolute;
    top: 0;
    left: 50%;
    width: var(--bar-width);
    height: 2px;
    translate: -50% 0;
    background: linear-gradient(90deg, transparent, #c3d0ff 20%, #ffffff 50%, #c3d0ff 80%, transparent);
    box-shadow: 0 0 22px 2px #8fa6ff;
    transition: width 1.4s cubic-bezier(0.16, 1, 0.3, 1) 0.2s;
  }
  .floor {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background: radial-gradient(ellipse 45% 55% at 50% 0%, #6c8cff26, transparent 70%);
  }
  :global(html[data-motion='paused']) .lamp {
    --cone-width: 30rem;
    --bar-width: 30rem;
    --halo-width: 16rem;
  }
  :global(html[data-motion='paused']) .lamp > * {
    transition: none;
  }
  :global(html[data-motion='paused']) .cone {
    opacity: 1;
  }
  @media (prefers-reduced-motion: reduce) {
    .lamp {
      --cone-width: 30rem;
      --bar-width: 30rem;
      --halo-width: 16rem;
    }
    .lamp > * {
      transition: none;
    }
    .cone {
      opacity: 1;
    }
  }
  @media (max-width: 760px) {
    .lamp.lit,
    :global(html[data-motion='paused']) .lamp {
      --cone-width: 11rem;
      --bar-width: 18rem;
      --halo-width: 10rem;
    }
    .lamp {
      --cone-width: 6rem;
      --bar-width: 9rem;
    }
  }
</style>
